import { useMemo } from "react";
import { Modal, Button, Typography, Space, Tag, Card, Collapse } from "antd";
import { IoCloseSharp } from "react-icons/io5";
import { MdDone } from "react-icons/md";
import {
  FiTag,
  FiBox,
  FiPackage,
  FiCpu,
  FiAlertTriangle,
} from "react-icons/fi";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const AssetSummaryCard = ({ icon, label, count, color }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "12px 16px",
      background: "#fff",
      borderRadius: 8,
      minWidth: 80,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    }}
  >
    <div style={{ color, marginBottom: 4 }}>{icon}</div>
    <Text style={{ fontSize: 20, fontWeight: 700, color: "#232A36" }}>
      {count}
    </Text>
    <Text style={{ fontSize: 12, color: "#718096", fontWeight: 500 }}>
      {label}
    </Text>
  </div>
);

const TagList = ({ items, getLabel }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
    }}
  >
    {items.map((item, idx) => {
      const label = getLabel ? getLabel(item) : item;
      return (
        <Tag
          key={label + idx}
          style={{
            background: "#F5F5F5",
            color: "#232A36",
            fontWeight: 600,
            fontSize: 12,
            borderRadius: 4,
            margin: 0,
            padding: "2px 8px",
            border: "none",
          }}
        >
          {label}
        </Tag>
      );
    })}
  </div>
);

function ModalAfterAssetCreation({ isOpen, isClose, kitData }) {
  // Group seals by type
  const sealsByType = useMemo(() => {
    const sampleSeals = kitData?.createdSampleSeals;
    if (!Array.isArray(sampleSeals)) return [];

    const grouped = {};
    sampleSeals.forEach((seal) => {
      const typeLabel = seal.sealType?.label || "Unknown";
      const typeId = seal.sealType?._id || "unknown";
      if (!grouped[typeId]) {
        grouped[typeId] = { label: typeLabel, seals: [] };
      }
      grouped[typeId].seals.push(seal.sampleSealNumber);
    });

    return Object.values(grouped);
  }, [kitData]);

  const totalSeals = useMemo(() => {
    return sealsByType.reduce((acc, group) => acc + group.seals.length, 0);
  }, [sealsByType]);

  // Failed seals grouped by type
  const failedSealsByType = useMemo(() => {
    const failedSeals = kitData?.failedSealNumbers;
    if (!Array.isArray(failedSeals)) return [];

    const grouped = {};
    failedSeals.forEach((seal) => {
      const typeLabel = seal.sealType || "Unknown";
      if (!grouped[typeLabel]) {
        grouped[typeLabel] = {
          label: typeLabel,
          seals: [],
          reason: seal.reason || "",
        };
      }
      grouped[typeLabel].seals.push(seal.sealNumber);
    });

    return Object.values(grouped);
  }, [kitData]);

  const totalFailedSeals = useMemo(() => {
    return failedSealsByType.reduce(
      (acc, group) => acc + group.seals.length,
      0
    );
  }, [failedSealsByType]);

  // Boxes
  const boxIds = useMemo(() => {
    const samplerBoxes = kitData?.createdSamplerBoxes;
    if (Array.isArray(samplerBoxes)) {
      return samplerBoxes.map((box) => box.boxId);
    }
    return [];
  }, [kitData]);

  const failedBoxes = useMemo(() => {
    const failedBoxNumbers = kitData?.failedBoxNumbers;
    if (Array.isArray(failedBoxNumbers)) {
      return failedBoxNumbers;
    }
    return [];
  }, [kitData]);

  // Stickers
  const stickerIds = useMemo(() => {
    const stickers = kitData?.createdSampleStickers;
    if (Array.isArray(stickers)) {
      return stickers.map((sticker) => sticker.sampleStickerNumber);
    }
    return [];
  }, [kitData]);

  const failedStickers = useMemo(() => {
    const failedStickerNumbers = kitData?.failedStickerNumbers;
    if (Array.isArray(failedStickerNumbers)) {
      return failedStickerNumbers;
    }
    return [];
  }, [kitData]);

  // Devices
  const devices = useMemo(() => {
    const deviceList = kitData?.createdDevices;
    if (Array.isArray(deviceList)) {
      return deviceList;
    }
    return [];
  }, [kitData]);

  const failedDevices = useMemo(() => {
    const failedDeviceList = kitData?.failedDevices;
    if (Array.isArray(failedDeviceList)) {
      return failedDeviceList;
    }
    return [];
  }, [kitData]);

  const hasSuccessItems =
    totalSeals > 0 ||
    boxIds.length > 0 ||
    stickerIds.length > 0 ||
    devices.length > 0;

  const hasFailedItems =
    totalFailedSeals > 0 ||
    failedBoxes.length > 0 ||
    failedStickers.length > 0 ||
    failedDevices.length > 0;

  return (
    <Modal
      visible={isOpen}
      onCancel={isClose}
      title={
        <Title
          level={4}
          style={{ fontWeight: 700, margin: 0, color: "#232A36" }}
        >
          Assets Created
        </Title>
      }
      footer={
        <Button
          key="close"
          type="primary"
          onClick={isClose}
          style={{
            width: 120,
            fontWeight: 700,
            borderRadius: 8,
          }}
        >
          Done
        </Button>
      }
      width={750}
      bodyStyle={{
        borderRadius: 16,
        padding: 20,
        background: "#fff",
        maxHeight: "70vh",
        overflowY: "auto",
      }}
      style={{ borderRadius: 16 }}
      closeIcon={<IoCloseSharp size="24" />}
      centered
      className="modal-content"
    >
      <Space direction="vertical" style={{ width: "100%" }} size={16}>
        {/* Success Card */}
        {hasSuccessItems && (
          <Card
            style={{
              background: "#EAF7EA",
              borderRadius: 8,
              textAlign: "center",
              border: "none",
            }}
            bodyStyle={{ padding: 20 }}
          >
            <MdDone size={36} color="#2E7D32" />
            <Title
              level={4}
              style={{ fontWeight: 800, marginBottom: 4, marginTop: 8 }}
            >
              Successfully Created!
            </Title>

            {/* Asset Summary Row */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 12,
                marginTop: 16,
                flexWrap: "wrap",
              }}
            >
              {totalSeals > 0 && (
                <AssetSummaryCard
                  icon={<FiTag size={20} />}
                  label="Seals"
                  count={totalSeals}
                  color="#1890ff"
                />
              )}
              {boxIds.length > 0 && (
                <AssetSummaryCard
                  icon={<FiBox size={20} />}
                  label="Boxes"
                  count={boxIds.length}
                  color="#52c41a"
                />
              )}
              {stickerIds.length > 0 && (
                <AssetSummaryCard
                  icon={<FiPackage size={20} />}
                  label="Stickers"
                  count={stickerIds.length}
                  color="#722ed1"
                />
              )}
              {devices.length > 0 && (
                <AssetSummaryCard
                  icon={<FiCpu size={20} />}
                  label="Devices"
                  count={devices.length}
                  color="#13c2c2"
                />
              )}
            </div>

            {/* Collapsible Details */}
            <Collapse
              ghost
              style={{
                background: "rgba(255,255,255,0.6)",
                borderRadius: 8,
                marginTop: 16,
                textAlign: "left",
              }}
            >
              {/* Seals Panel */}
              {sealsByType.length > 0 && (
                <Panel
                  header={
                    <Text style={{ fontWeight: 600, color: "#232A36" }}>
                      <FiTag style={{ marginRight: 8 }} />
                      Seal Details ({totalSeals})
                    </Text>
                  }
                  key="seals"
                >
                  {sealsByType.map((group, groupIdx) => (
                    <div key={groupIdx} style={{ marginBottom: 12 }}>
                      <Text
                        style={{
                          fontWeight: 600,
                          color: "#718096",
                          fontSize: 13,
                          display: "block",
                          marginBottom: 6,
                        }}
                      >
                        {group.label} ({group.seals.length})
                      </Text>
                      <TagList items={group.seals} />
                    </div>
                  ))}
                </Panel>
              )}

              {/* Boxes Panel */}
              {boxIds.length > 0 && (
                <Panel
                  header={
                    <Text style={{ fontWeight: 600, color: "#232A36" }}>
                      <FiBox style={{ marginRight: 8 }} />
                      Box Details ({boxIds.length})
                    </Text>
                  }
                  key="boxes"
                >
                  <TagList items={boxIds} />
                </Panel>
              )}

              {/* Stickers Panel */}
              {stickerIds.length > 0 && (
                <Panel
                  header={
                    <Text style={{ fontWeight: 600, color: "#232A36" }}>
                      <FiPackage style={{ marginRight: 8 }} />
                      Sticker Details ({stickerIds.length})
                    </Text>
                  }
                  key="stickers"
                >
                  <TagList items={stickerIds} />
                </Panel>
              )}

              {/* Devices Panel */}
              {devices.length > 0 && (
                <Panel
                  header={
                    <Text style={{ fontWeight: 600, color: "#232A36" }}>
                      <FiCpu style={{ marginRight: 8 }} />
                      Device Details ({devices.length})
                    </Text>
                  }
                  key="devices"
                >
                  <TagList
                    items={devices}
                    getLabel={(d) => `${d.deviceName} (${d.uniqueId})`}
                  />
                </Panel>
              )}
            </Collapse>
          </Card>
        )}

        {/* Failed Items Card */}
        {hasFailedItems && (
          <Card
            style={{
              background: "#FFF2F0",
              borderRadius: 8,
              border: "1px solid #FFCCC7",
            }}
            bodyStyle={{ padding: 16 }}
          >
            <Text
              style={{
                fontWeight: 700,
                fontSize: 15,
                color: "#CF1322",
                display: "block",
                marginBottom: 12,
              }}
            >
              <FiAlertTriangle
                style={{ marginRight: 8, verticalAlign: "middle" }}
              />
              Some items failed to add
            </Text>

            <Collapse
              ghost
              style={{
                background: "rgba(255,255,255,0.6)",
                borderRadius: 8,
                textAlign: "left",
              }}
            >
              {/* Failed Seals Panel */}
              {totalFailedSeals > 0 && (
                <Panel
                  header={
                    <Text style={{ fontWeight: 600, color: "#CF1322" }}>
                      <FiTag style={{ marginRight: 8 }} />
                      Failed Seals ({totalFailedSeals})
                      {failedSealsByType[0]?.reason
                        ? ` - ${failedSealsByType[0].reason}`
                        : ""}
                    </Text>
                  }
                  key="failed-seals"
                >
                  {failedSealsByType.map((group, groupIdx) => (
                    <div key={groupIdx} style={{ marginBottom: 12 }}>
                      <Text
                        style={{
                          fontWeight: 600,
                          color: "#718096",
                          fontSize: 13,
                          display: "block",
                          marginBottom: 6,
                        }}
                      >
                        {group.label} ({group.seals.length})
                      </Text>
                      <TagList items={group.seals} />
                    </div>
                  ))}
                </Panel>
              )}

              {/* Failed Boxes Panel */}
              {failedBoxes.length > 0 && (
                <Panel
                  header={
                    <Text style={{ fontWeight: 600, color: "#CF1322" }}>
                      <FiBox style={{ marginRight: 8 }} />
                      Failed Boxes ({failedBoxes.length})
                      {failedBoxes[0]?.reason
                        ? ` - ${failedBoxes[0].reason}`
                        : ""}
                    </Text>
                  }
                  key="failed-boxes"
                >
                  <TagList
                    items={failedBoxes}
                    getLabel={(box) => box.boxNumber}
                  />
                </Panel>
              )}

              {/* Failed Stickers Panel */}
              {failedStickers.length > 0 && (
                <Panel
                  header={
                    <Text style={{ fontWeight: 600, color: "#CF1322" }}>
                      <FiPackage style={{ marginRight: 8 }} />
                      Failed Stickers ({failedStickers.length})
                      {failedStickers[0]?.reason
                        ? ` - ${failedStickers[0].reason}`
                        : ""}
                    </Text>
                  }
                  key="failed-stickers"
                >
                  <TagList
                    items={failedStickers}
                    getLabel={(s) => s.stickerNumber}
                  />
                </Panel>
              )}

              {/* Failed Devices Panel */}
              {failedDevices.length > 0 && (
                <Panel
                  header={
                    <Text style={{ fontWeight: 600, color: "#CF1322" }}>
                      <FiCpu style={{ marginRight: 8 }} />
                      Failed Devices ({failedDevices.length})
                      {failedDevices[0]?.reason
                        ? ` - ${failedDevices[0].reason}`
                        : ""}
                    </Text>
                  }
                  key="failed-devices"
                >
                  <TagList
                    items={failedDevices}
                    getLabel={(d) => `${d.deviceName} (${d.uniqueId})`}
                  />
                </Panel>
              )}
            </Collapse>
          </Card>
        )}
      </Space>
    </Modal>
  );
}

export default ModalAfterAssetCreation;
